/**
 * AddProviderPage — form for creating a new provider (Feature C).
 *
 * Sub-stories implemented here:
 *   C1 — Four-field create form: Name, Short Code (auto-generated), Type, Schemas.
 *   C2 — On-blur uniqueness validation for Name and Short Code via dedicated backend endpoints.
 *   C3 — "Save as Draft" — POST provider, assign schemas, navigate to profile with ?banner=draft.
 *   C4 — "Save and Activate" — POST provider, assign schemas, activate status, navigate with
 *         ?banner=activated.
 *   C5 — "Cancel" with discard confirmation if any field has been touched.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ProviderFormFields } from "@/components/providers/ProviderFormFields";
import type { UniquenessState } from "@/components/providers/ProviderFormFields";
import { Button, AlertMessage, ConfirmDialog } from "@/components/reusable";
import {
  createProvider,
  transitionProviderStatus,
  addSchemaAssignment,
  checkProviderName,
  checkProviderShortCode,
} from "@/services/providersApi";
import { listSchemas, toAssignableTargetSchemas } from "@/services/schemaBuilderApi";
import { useTelemetry } from "@/hooks/useTelemetry";
import { useRefreshCurrentUser } from "@/hooks/useCurrentUser";
import type { TargetSchema } from "@/types";
import type { ProviderType } from "@/types/providers";

// ── Regex matching the backend short code validation rule ─────────────────────
const SHORT_CODE_RE = /^[A-Z0-9-]{1,10}$/;

/**
 * Derives an auto-suggested short code from a provider name.
 * Strips non-alphanumeric characters, takes the first 10, uppercases — matching
 * the 10-char short-code limit. The user can override this at any point.
 */
function generateShortCode(name: string): string {
  return name
    .replace(/[^A-Z0-9]/gi, "")
    .slice(0, 10)
    .toUpperCase();
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function AddProviderPage() {
  const navigate = useNavigate();
  const { track, trackError } = useTelemetry();
  const refreshCurrentUser = useRefreshCurrentUser();

  // ── Form state ──────────────────────────────────────────────────────────────
  const [providerName, setProviderName] = useState("");
  const [shortCode, setShortCode] = useState("");
  const [isShortCodeManual, setIsShortCodeManual] = useState(false);
  const [providerType, setProviderType] = useState<ProviderType | "">("");
  const [selectedSchemaIds, setSelectedSchemaIds] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  // ── Schemas list ────────────────────────────────────────────────────────────
  const [schemas, setSchemas] = useState<TargetSchema[]>([]);
  const [schemasLoading, setSchemasLoading] = useState(true);

  // ── Uniqueness validation ───────────────────────────────────────────────────
  const [nameUniqueness, setNameUniqueness] = useState<UniquenessState>("idle");
  const [shortCodeUniqueness, setShortCodeUniqueness] = useState<UniquenessState>("idle");
  const [shortCodeFormatError, setShortCodeFormatError] = useState<string | null>(null);

  // ── Submission ──────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── Discard guard (C5) ──────────────────────────────────────────────────────
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // ── Load schemas on mount (v2 list — includes preset vs published status) ───
  useEffect(() => {
    listSchemas()
      .then((items) => setSchemas(toAssignableTargetSchemas(items)))
      .catch((err) => trackError(err, { context: "add_provider_schemas_load" }))
      .finally(() => setSchemasLoading(false));
  }, [trackError]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  /**
   * When the provider name changes, auto-suggest the short code unless the user
   * has already edited it manually.
   */
  const handleNameChange = useCallback(
    (value: string) => {
      setProviderName(value);
      if (!isShortCodeManual) {
        setShortCode(generateShortCode(value));
        setShortCodeUniqueness("idle");
        setShortCodeFormatError(null);
      }
      setNameUniqueness("idle");
      setIsDirty(true);
    },
    [isShortCodeManual]
  );

  /** Manual short code edit — marks it as user-owned and resets validation. */
  const handleShortCodeChange = useCallback((value: string) => {
    setIsShortCodeManual(true);
    setShortCode(value);
    setShortCodeUniqueness("idle");
    setShortCodeFormatError(null);
    setIsDirty(true);
  }, []);

  const handleProviderTypeChange = useCallback((value: ProviderType) => {
    setProviderType(value);
    setIsDirty(true);
  }, []);

  const handleSchemaToggle = useCallback((schemaId: string) => {
    setSelectedSchemaIds((prev) =>
      prev.includes(schemaId) ? prev.filter((id) => id !== schemaId) : [...prev, schemaId]
    );
    setIsDirty(true);
  }, []);

  /** C2 — Uniqueness check on Provider Name blur. */
  const handleNameBlur = useCallback(async () => {
    const trimmed = providerName.trim();
    if (!trimmed) return;
    setNameUniqueness("checking");
    try {
      const result = await checkProviderName(trimmed);
      setNameUniqueness(result.available ? "available" : "taken");
    } catch {
      setNameUniqueness("idle");
    }
  }, [providerName]);

  /** C2 — Format validation + uniqueness check on Short Code blur. */
  const handleShortCodeBlur = useCallback(async () => {
    const trimmed = shortCode.trim();
    if (!trimmed) return;

    if (!SHORT_CODE_RE.test(trimmed)) {
      setShortCodeFormatError("Must be 1–10 uppercase letters, numbers, or hyphens.");
      setShortCodeUniqueness("idle");
      return;
    }
    setShortCodeFormatError(null);
    setShortCodeUniqueness("checking");
    try {
      const result = await checkProviderShortCode(trimmed);
      setShortCodeUniqueness(result.available ? "available" : "taken");
    } catch {
      setShortCodeUniqueness("idle");
    }
  }, [shortCode]);

  // ── Derived validation ───────────────────────────────────────────────────────
  const hasBlockingErrors =
    nameUniqueness === "taken" || shortCodeUniqueness === "taken" || !!shortCodeFormatError;

  const canSubmit =
    providerName.trim().length > 0 &&
    shortCode.trim().length > 0 &&
    providerType !== "" &&
    !hasBlockingErrors &&
    !submitting;

  // Activate requires at least one schema (AC-03); draft does not.
  const canActivate = canSubmit && selectedSchemaIds.length > 0;

  // ── Submit helpers ────────────────────────────────────────────────────────────

  /**
   * Creates the provider then sequentially adds each schema assignment.
   * Schema assignment failures are tolerated — the provider is still created
   * and the user can add schemas from the profile page.
   */
  async function createProviderWithSchemas() {
    const provider = await createProvider({
      provider_name: providerName.trim(),
      provider_short_code: shortCode.trim(),
      provider_type: providerType as ProviderType,
    });

    for (const schemaId of selectedSchemaIds) {
      try {
        await addSchemaAssignment(provider.provider_id, schemaId);
      } catch (err) {
        trackError(err instanceof Error ? err : new Error(String(err)), {
          context: "add_provider_schema_assignment",
          schemaId,
        });
      }
    }

    // Refresh /me providerIds so Home / upload dropdowns see the new provider
    // without a full page reload (admins get all provider IDs from /me).
    try {
      await refreshCurrentUser();
    } catch (err) {
      trackError(err instanceof Error ? err : new Error(String(err)), {
        context: "add_provider_refresh_current_user",
      });
    }

    return provider;
  }

  /** C3 — Save provider as PENDING draft. */
  async function handleSaveAsDraft() {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const provider = await createProviderWithSchemas();
      track("provider_created", {
        providerId: provider.provider_id,
        schemaCount: selectedSchemaIds.length,
        action: "draft",
      });
      navigate(`/provider-management/providers/${provider.provider_id}?banner=draft`);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create provider.";
      setSubmitError(message);
      trackError(err instanceof Error ? err : new Error(message), {
        context: "add_provider_save_draft",
      });
    } finally {
      setSubmitting(false);
    }
  }

  /** C4 — Save provider then immediately activate it. */
  async function handleSaveAndActivate() {
    if (!canActivate) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const provider = await createProviderWithSchemas();
      await transitionProviderStatus(provider.provider_id, "active");
      try {
        await refreshCurrentUser();
      } catch (err) {
        trackError(err instanceof Error ? err : new Error(String(err)), {
          context: "add_provider_refresh_after_activate",
        });
      }
      track("provider_created", {
        providerId: provider.provider_id,
        schemaCount: selectedSchemaIds.length,
        action: "activate",
      });
      navigate(`/provider-management/providers/${provider.provider_id}?banner=activated`);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create or activate provider.";
      setSubmitError(message);
      trackError(err instanceof Error ? err : new Error(message), {
        context: "add_provider_save_activate",
      });
    } finally {
      setSubmitting(false);
    }
  }

  /** C5 — Navigate back, showing a discard confirmation if the form is dirty. */
  function handleCancel() {
    if (isDirty) {
      setDiscardConfirmOpen(true);
    } else {
      navigate("/provider-management/providers");
    }
  }

  function handleConfirmDiscard() {
    setDiscardConfirmOpen(false);
    navigate("/provider-management/providers");
  }

  return (
    <div className="min-h-[calc(100vh-72px)] animate-fade-in">
      <section className="mx-auto max-w-2xl px-6 py-10">
        <button
          type="button"
          onClick={handleCancel}
          className="mb-8 flex items-center gap-2 text-sm font-medium text-[var(--color-text-primary)] transition hover:text-[var(--color-brand-primary)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Providers
        </button>

        <h1 className="mb-1 text-3xl font-semibold tracking-tight text-[var(--color-text-primary)]">
          Add New Provider
        </h1>
        <p className="mb-8 text-sm text-[var(--color-text-secondary)]">
          Fill in the details below. You can save as a draft to review later, or activate
          immediately.
        </p>

        {submitError && (
          <div className="mb-6">
            <AlertMessage variant="error" message={submitError} />
          </div>
        )}

        <ProviderFormFields
          name={providerName}
          onNameChange={handleNameChange}
          onNameBlur={handleNameBlur}
          nameUniqueness={nameUniqueness}
          nameError={null}
          shortCode={shortCode}
          onShortCodeChange={handleShortCodeChange}
          onShortCodeBlur={handleShortCodeBlur}
          shortCodeUniqueness={shortCodeUniqueness}
          shortCodeError={shortCodeFormatError}
          providerType={providerType}
          onProviderTypeChange={handleProviderTypeChange}
          selectedSchemaIds={selectedSchemaIds}
          onSchemaToggle={handleSchemaToggle}
          schemas={schemas}
          schemasLoading={schemasLoading}
          disabled={submitting}
        />

        {canSubmit && selectedSchemaIds.length === 0 && (
          <p className="mt-4 text-xs text-amber-700">
            Assign at least one schema to enable Save &amp; Activate. You can still save as a draft.
          </p>
        )}

        <div className="mt-10 flex items-center justify-between gap-3 border-t border-[var(--color-border-light)] pt-6">
          <Button type="button" variant="ghost" onClick={handleCancel} disabled={submitting}>
            Cancel
          </Button>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={handleSaveAsDraft}
              disabled={!canSubmit}
            >
              {submitting ? "Saving…" : "Save as Draft"}
            </Button>

            <Button
              type="button"
              variant="primary"
              onClick={handleSaveAndActivate}
              disabled={!canActivate}
              title={
                selectedSchemaIds.length === 0
                  ? "Assign at least one schema to activate"
                  : undefined
              }
            >
              {submitting ? "Saving…" : "Save & Activate"}
            </Button>
          </div>
        </div>
      </section>

      <ConfirmDialog
        open={discardConfirmOpen}
        onOpenChange={setDiscardConfirmOpen}
        title="Discard changes?"
        description="You have unsaved changes. If you go back now your progress will be lost."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        tone="warning"
        onConfirm={handleConfirmDiscard}
      />
    </div>
  );
}
